import { showSuccessToast } from 'shared/lib/notifications';
import type { ContractorGetCampaignModel } from 'entities/campaign';
import { useSubmitZips, useUpdateCampaign } from 'entities/campaign';
import { useModalsActions } from 'shared/ui/SideModal';
import { useZipsNumber } from 'features/edit-zips';
import { useGetContractor } from 'entities/profile';
import { Roles, useClientDataStore } from 'shared/lib/store';

import { BudgetExitModal } from '../BudgetExitModal';
import type { CampaignEditModalBodyProps, CampaignEditModel } from '../model';

import { getUpdateCampaignParams, isBudgetChangeAlertShown, sendBudgetChangeEvent } from './utils';

type UseSaveCampaignType = {
  campaign: CampaignEditModalBodyProps['campaign'];
  skipCampaignRefetch?: CampaignEditModalBodyProps['skipCampaignRefetch'];
  afterSuccessSaveHandler?: CampaignEditModalBodyProps['afterSuccessSaveHandler'];
  isManagerCampaign: boolean;
};

export function useSaveCampaign({
  campaign,
  skipCampaignRefetch,
  afterSuccessSaveHandler,
  isManagerCampaign,
}: UseSaveCampaignType) {
  const userData = useGetContractor().data?.data;
  const submitZipsQuery = useSubmitZips(campaign.id);
  const zipsNumber = useZipsNumber();
  const { forceClose, open } = useModalsActions();
  const updateCampaignQuery = useUpdateCampaign(campaign.id, true, skipCampaignRefetch);
  const { getRole } = useClientDataStore();
  const isContractor = getRole() === Roles.Contractor;

  return {
    updateCampaignError: updateCampaignQuery.error,
    isCampaignUpdating: updateCampaignQuery.isLoading || submitZipsQuery.isLoading,
    saveCampaign: (form: CampaignEditModel) => {
      const handleUpdate = () => {
        updateCampaignQuery.mutate(
          { payload: getUpdateCampaignParams({ form, campaign }) },
          {
            onSuccess: () => {
              showSuccessToast('Your changes were saved successfully');
              forceClose();

              if (afterSuccessSaveHandler) {
                afterSuccessSaveHandler();
              }

              if (isContractor) {
                sendBudgetChangeEvent({
                  event: 'confirm_budget',
                  userData,
                  campaignData: campaign,
                  newBudget: form.budget,
                });
              }
            },
          }
        );
        if (zipsNumber) {
          submitZipsQuery.mutate();
        }
      };

      if (
        !isManagerCampaign &&
        isBudgetChangeAlertShown(
          form.budget,
          campaign.budget?.amount,
          Number((campaign as ContractorGetCampaignModel).settings.validationRules.campaignsMinimalBudgetValue.amount)
        )
      ) {
        open(
          <BudgetExitModal
            budget={form.budget}
            onConfirm={handleUpdate}
            onCancel={() => {
              if (isContractor) {
                sendBudgetChangeEvent({
                  event: 'edit_budget',
                  userData,
                  campaignData: campaign,
                  newBudget: form.budget,
                });
              }
            }}
          />
        );
      } else {
        handleUpdate();
      }
    },
  };
}
